import { Inject, Injectable, OnInit } from '@angular/core';
import { LanguageService } from './language.service';
import { HttpClient } from '@angular/common/http';
import { v4 as uuidv4 } from 'uuid';
import { SettingsService } from './settings.service';
import { de } from 'intl-tel-input/i18n';
import { appPhase } from '../enums';
import { INotification, IRoomOccupancy, IExtraCartItem } from '../interfaces';
import { DebugService } from './debug.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  IPimNotification,
  IPimNotificationContext,
  IPimNotificationRule,
  IPimNotificationText,
  IPimNotificationDisplay,
  NotificationPhase,
  NotificationTarget,
  EMPTY_PIM_NOTIFICATION_CONTEXT,
} from '../pim-notification-interface';
import { PIMService } from './pim.service';
import { appPhases } from '../types';
import { AvailabilityService } from './availability.service';
import { ShopcartService } from './shopcart.service';

dayjs.extend(utc);
dayjs.extend(timezone);

const HELSINKI_TZ = 'Europe/Helsinki';
const DAY_BITS = [1, 2, 4, 8, 16, 32, 64]; // Mon..Sun

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  notifications:INotification[];

  constructor(
    private languageService: LanguageService,
    private http: HttpClient,
    private settings: SettingsService,
    private debug: DebugService,
    private avService: AvailabilityService,
    private pimService: PIMService,
    private shopCart: ShopcartService,

    @Inject('environment')
    private environment: {[key: string]: string}
  ) {

  }

  onAppInit() {
/*TODO, move getting notifications to here */

    return new Promise<boolean>((resolve , reject) => {

      let endpoint = this.settings.getEndpoint();
      this.http.get(`${endpoint}/Content/json/notifications`, {
        responseType: "json",
        headers: {
          'X-Conversation-ID': uuidv4()
        }
      }).subscribe({
        next: (res:any) => {
          this.debug.log('Notifications response', res?.notifications);

          if (res && res.notifications) {
            this.notifications = res.notifications;
          }
            resolve(true);
          },
        error: (error) => {
          console.log('Error getting notifications: ',error)
          // do not cancel loading even if notification gives error
          resolve(true);
        }
      });      
    });
  }

  getAllNotificationsForPhase(phase: appPhases) {

    let language = this.languageService.getCurrentLanguage();
    if (!this.notifications) {
      return [];
    }
    let notificationsForPhase = this.notifications.filter((notification:INotification) => {
      return notification.phases.includes(phase);
    });

    // return only the current language
    notificationsForPhase.forEach((notification) => {
      if (Array.isArray(notification.title)) {
        let notifTitleObj = notification.title.find((item:any) => item.lang == language);
        notification.title = notifTitleObj?.text ?? "";
      }
      if (Array.isArray(notification.description)) {
        let notifDescObj = notification.description.find((item:any) => item.lang == language);
        notification.description = notifDescObj?.text ?? "";
      }
      
    });

    this.debug.log('All notification for phase', phase, notificationsForPhase); 
    
    return notificationsForPhase;
  }

  getfilteredNotificationsForPhase(phase: appPhases) {

      let notifications = this.getAllNotificationsForPhase(phase).filter((notification:INotification) => {

        let isValidDates:boolean = this.validateDates(notification);

        let isValidSearchMask:boolean = this.validateSearchMask(notification);
        if (phase == 'home' || phase == 'datepicker') {
          // do not check searchmask in these phases
          isValidSearchMask = true;
        }
        
        this.debug.log('filtered notification for phase',phase, notification.title, "isValidDates", isValidDates, "isValidSearchMask", isValidSearchMask);

        return isValidDates && isValidSearchMask;
      }); 
      return notifications;
  }

  validateDates(notification:INotification) {
    let now = dayjs();
    let start = dayjs(notification.startDate);
    let end = dayjs(notification.endDate);
    return (now.isAfter(start) && now.isBefore(end))
  } 

  validateSearchMask(notification: INotification) {
    let isValid:boolean = true;
    let sm = this.avService.getSearchParams();

    if (sm) {
      let isValidArrival = !notification.arrival || this.validateSmDate(notification.arrival.value, sm.arrival, notification.arrival.operator);
      let isValidDeparture = !notification.departure || this.validateSmDate(notification.departure.value, sm.departure, notification.departure.operator);

      let isValidAdults = false; 
      let isValidChildren = false;
      sm.roomData.forEach((room) => {
        if (!notification.adults || this.validateSmNumber(notification.adults.value, room.adults, notification.adults.operator)) {
          isValidAdults = true;
        }
        if (!notification.children || this.validateSmNumber(notification.children.value, room.children, notification.children.operator)) {
          isValidChildren = true;
        }
      });
            
      let isValidNumberOfRooms = !notification.numberOfRooms || this.validateSmNumber(notification.numberOfRooms.value, sm.roomData.length, notification.numberOfRooms.operator);
      let isValidBookingCode = !notification.bookingCodes || notification.bookingCodes.includes(sm.bookingCode);
      let isValidHotelCode = !notification.hotelCodes || notification.hotelCodes?.length == 0 || notification.hotelCodes.includes(sm.hotelCode);

    /*   console.log("\nValidate notifications", notification.title);
      console.log('isValidArrival', isValidArrival, notification.arrival?.value, sm.arrival, notification.arrival?.operator);
      console.log('isValidDeparture', isValidDeparture, notification.departure?.value, sm.departure, notification.departure?.operator);
      console.log('isValidAdults', isValidAdults, notification.adults?.value, sm.roomData, notification.adults?.operator);
      console.log('isValidChildren', isValidChildren, notification.children?.value, sm.roomData, notification.children?.operator);
      console.log('isValidNumberOfRooms', isValidNumberOfRooms, notification.numberOfRooms?.value, sm.roomData.length, notification.numberOfRooms?.operator);
      console.log('isValidBookingCode', isValidBookingCode, notification.bookingCodes, sm.bookingCode);
      console.log('isValidHotelCode', isValidHotelCode, notification.hotelCodes, sm.hotelCode); */


      isValid = isValidArrival && isValidDeparture && isValidAdults && isValidChildren && isValidNumberOfRooms && isValidBookingCode && isValidHotelCode;
    }
    
    return isValid;
  }

  validateSmDate(notifDate:string, smDate:string|Date, operator:string) {

    let isValid:boolean = false;
    switch (operator) {
      case "<":
        isValid = dayjs(smDate).isBefore(dayjs(notifDate));
        break;
      case ">":
        isValid = dayjs(smDate).isAfter(dayjs(notifDate));
        break;
      case "=":
        isValid = dayjs(smDate).isSame(dayjs(notifDate), 'day');
        break;
      default:
        break;
    }
    return isValid;
  }

  validateSmNumber(notifNumber:number, smNumber:number, operator:string) {
    let isValid:boolean = false;
    switch (operator) {
      case "<":
        isValid = smNumber < notifNumber;
        break;
      case ">":
        isValid = smNumber > notifNumber;
        break;
      case "=":
        isValid = smNumber == notifNumber;
        break;
      default:
        break;
    }
    return isValid;
  }

  /** Filter PIM2 notifications for a phase/target, before text resolution. */
  matchPim2Notifications(
    phase: string,
    target: string,
    context: IPimNotificationContext
  ): IPimNotification[] {
    const now = dayjs().tz(HELSINKI_TZ);
    const dayIndex = (now.day() + 6) % 7; // Mon = 0

    return this.getAllPim2Notifications(context.selectedHotelCode ?? undefined).filter((n) => {
      // target
      if (n.target !== target) return false;

      // phase ([] = all)
      if (n.phases?.length > 0 && !n.phases.includes(phase as any)) return false;

      // validity dates (instant comparison; PIM datetimes are UTC)
      if (n.validityStartDate && now.isBefore(dayjs.utc(n.validityStartDate))) return false;
      if (n.validityEndDate && now.isAfter(dayjs.utc(n.validityEndDate))) return false;

      // stay dates — compare against selected arrival as calendar dates (Helsinki day)
      if (context.selectedArrival) {
        const arrival = dayjs(context.selectedArrival).tz(HELSINKI_TZ).startOf('day');
        if (n.stayStart && arrival.isBefore(dayjs.utc(n.stayStart).tz(HELSINKI_TZ).startOf('day'))) return false;
        if (n.stayEnd && arrival.isAfter(dayjs.utc(n.stayEnd).tz(HELSINKI_TZ).startOf('day'))) return false;
      }

      // weekday bitmask (Helsinki day-of-week); an absent field means "all days"
      if (typeof n.weekdays === 'number') {
        if (n.weekdays === 0) return false; // explicitly no days
        if ((n.weekdays & DAY_BITS[dayIndex]) === 0) return false;
      }

      // rules (all must pass — AND)
      return (n.rules ?? []).every((rule) => this.evaluatePim2Rule(rule, context));
    });
  }

  /**
   * Collect PIM2 notifications, deduped by id.
   * - hotelCode provided → only that hotel's notifications
   * - hotelCode omitted  → notifications from all hotels in app.endpoints (this booking engine)
   */
  getAllPim2Notifications(hotelCode?: string): IPimNotification[] {
    const pimData: any[] = this.pimService.pimData;
    if (!Array.isArray(pimData)) {
      return [];
    }

    let allowedCodes: Set<string> | null = null;
    if (hotelCode) {
      allowedCodes = new Set([hotelCode]);
    } else {
      const endpoints: any[] = this.settings.get('app.endpoints') ?? [];
      if (endpoints.length > 0) {
        allowedCodes = new Set(endpoints.map((ep: any) => ep.hotelCode).filter(Boolean));
      }
    }

    const byId = new Map<string, IPimNotification>();
    pimData.forEach((hotel: any) => {
      if (allowedCodes && !allowedCodes.has(hotel?.hotelCode)) return;
      (hotel?.notifications ?? []).forEach((n: IPimNotification) => {
        if (n && n.id && !byId.has(n.id)) {
          byId.set(n.id, n);
        }
      });
    });
    return Array.from(byId.values());
  }

  private evaluatePim2Rule(rule: IPimNotificationRule, context: IPimNotificationContext): boolean {
    if (rule.ruleType === 'CodeRule') {
      const ruleCodes = rule.value.split(',').map((c) => c.trim());
      const current = this.getPim2CodeValues(rule.name, context);
      if (current.length === 0) return false; // nothing selected yet
      // "any cart item matches": IS_IN shows if any selected code is listed;
      // NOT_IN shows only if NO selected code is listed (suppress as soon as one
      // selected code is in the exclusion list, e.g. "show unless guest booked X").
      if (rule.operator === 'IS_IN') return current.some((c) => ruleCodes.includes(c));
      if (rule.operator === 'NOT_IN') return current.every((c) => !ruleCodes.includes(c));
      return false;
    }

    if (rule.ruleType === 'SearchMaskRule') {
      // CATEGORY is a string field — handled separately from the numeric fields below
      if (rule.name === 'CATEGORY') {
        const current = context.searchMask.category;
        if (current === null) return false; // not yet selected
        if (rule.operator === 'EQUAL') return current === rule.value;
        if (rule.operator === 'NOT_EQUAL') return current !== rule.value;
        return false;
      }
      if (rule.name === 'ARRIVAL_DATE') return this.evaluateDateRule(context.selectedArrival, rule);
      if (rule.name === 'DEPARTURE_DATE') return this.evaluateDateRule(context.selectedDeparture, rule);
      if (rule.name === 'WEEKDAY') return this.evaluateWeekdayRule(context, rule);
      const current = this.getPim2SearchMaskNumeric(rule.name, context);
      if (current === null) return false; // search not submitted
      const threshold = Number(rule.value);
      if (isNaN(threshold)) return false; // malformed numeric rule value
      switch (rule.operator) {
        case 'LESS_THAN': return current < threshold;
        case 'LESS_THAN_OR_EQUAL': return current <= threshold;
        case 'MORE_THAN': return current > threshold;
        case 'MORE_THAN_OR_EQUAL': return current >= threshold;
        case 'EQUAL': return current === threshold;
        case 'NOT_EQUAL': return current !== threshold;
        default: return false;
      }
    }
    return false;
  }

  /** Compare a selected date (Helsinki day) against rule.value using the operator. */
  private evaluateDateRule(dateVal: Date | string | null, rule: IPimNotificationRule): boolean {
    if (!dateVal) return false; // not selected yet
    const threshold = dayjs.utc(rule.value).tz(HELSINKI_TZ).startOf('day');
    if (!threshold.isValid()) return false;
    const current = dayjs(dateVal).tz(HELSINKI_TZ).startOf('day');
    switch (rule.operator) {
      case 'LESS_THAN': return current.isBefore(threshold);
      case 'LESS_THAN_OR_EQUAL': return current.isBefore(threshold) || current.isSame(threshold, 'day');
      case 'MORE_THAN': return current.isAfter(threshold);
      case 'MORE_THAN_OR_EQUAL': return current.isAfter(threshold) || current.isSame(threshold, 'day');
      case 'EQUAL': return current.isSame(threshold, 'day');
      case 'NOT_EQUAL': return !current.isSame(threshold, 'day');
      default: return false;
    }
  }

  /** WEEKDAY: rule.value is a Mon=1..Sun=64 bitmask; match if the stay (arrival..departure inclusive) overlaps any set day. */
  private evaluateWeekdayRule(context: IPimNotificationContext, rule: IPimNotificationRule): boolean {
    const mask = Number(rule.value);
    if (!mask || isNaN(mask)) return false;
    if (!context.selectedArrival) return false;
    const start = dayjs(context.selectedArrival).tz(HELSINKI_TZ).startOf('day');
    const end = context.selectedDeparture
      ? dayjs(context.selectedDeparture).tz(HELSINKI_TZ).startOf('day')
      : start;
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) return false;
    let stayMask = 0;
    let cursor = start;
    for (let i = 0; i <= 366 && !cursor.isAfter(end); i++) {
      const dayIndex = (cursor.day() + 6) % 7; // Mon = 0
      stayMask |= DAY_BITS[dayIndex];
      if (stayMask >= 127) break; // all weekdays already covered
      cursor = cursor.add(1, 'day');
    }
    return (stayMask & mask) !== 0;
  }

  private getPim2CodeValues(name: string, context: IPimNotificationContext): string[] {
    switch (name) {
      case 'RATECODE': return context.selectedRateCode;
      case 'ROOMCODE': return context.selectedRoomCode;
      case 'RATECATEGORY': return context.selectedRateCategory;
      case 'ROOMCATEGORY': return context.selectedRoomCategory;
      case 'EXTRACODE': return context.selectedExtraCode;
      case 'ALLOTMENTCODE': return context.selectedAllotmentCode;
      case 'DEPARTMENTCODE': return context.selectedDepartmentCode;
      case 'CAMPAIGNCODE': return context.selectedCampaignCode;
      default: return [];
    }
  }

  private getPim2SearchMaskNumeric(name: string, context: IPimNotificationContext): number | null {
    switch (name) {
      case 'ADULTS': return context.searchMask.adults;
      case 'PERSONS': return context.searchMask.persons;
      case 'CHILDREN': return context.searchMask.children;
      case 'CHILD_AGE': return context.searchMask.childAge;
      case 'NRO_OF_ROOMS': return context.searchMask.nroOfRooms;
      default: return null;
    }
  }

  /** Public entry point: matched + text-resolved PIM2 notifications for a phase. */
  getPim2NotificationsForPhase(
    phase: NotificationPhase,
    target: NotificationTarget,
    context: IPimNotificationContext
  ): IPimNotificationDisplay[] {
    const language = this.languageService.getCurrentLanguage();
    const results: (IPimNotificationDisplay | null)[] = this.matchPim2Notifications(phase, target, context)
      .map((n): IPimNotificationDisplay | null => {
        const text = this.resolvePim2Text(n.texts, language);
        if (!text) return null;
        const display: IPimNotificationDisplay = { id: n.id, title: text.title ?? '', message: text.message ?? null };
        if (n.color !== undefined) display.color = n.color;
        return display;
      });
    return results.filter((x): x is IPimNotificationDisplay => x !== null);
  }

  private resolvePim2Text(texts: IPimNotificationText[], language: string): IPimNotificationText | null {
    if (!texts || texts.length === 0) return null;
    // Fallback order: current language → fi → en → first available; null only if empty.
    return texts.find((t) => t.lang === language)
      ?? texts.find((t) => t.lang === 'fi')
      ?? texts.find((t) => t.lang === 'en')
      ?? texts[0];
  }

  /** Build a PIM2 evaluation context from the current Wbe search mask. */
  buildWbeContext(): IPimNotificationContext {
    const ctx: IPimNotificationContext = JSON.parse(JSON.stringify(EMPTY_PIM_NOTIFICATION_CONTEXT));
    const sm = this.avService.getSearchParams();
    if (sm) {
      const rooms: IRoomOccupancy[] = sm.roomData ?? [];
      const adults = rooms.reduce((sum, r) => sum + (r.adults ?? 0), 0);
      const children = rooms.reduce((sum, r) => sum + (r.children ?? 0), 0);
      const ages = rooms.flatMap((r) => r.childAges ?? []).filter((a): a is number => a !== null);

      ctx.selectedHotelCode = sm.hotelCode ?? null;
      ctx.selectedArrival = sm.arrival ?? null;
      ctx.selectedDeparture = sm.departure ?? null;
      ctx.searchMask.adults = rooms.length ? adults : null;
      ctx.searchMask.children = rooms.length ? children : null;
      ctx.searchMask.persons = rooms.length ? adults + children : null;
      ctx.searchMask.childAge = ages.length ? Math.min(...ages) : null;
      ctx.searchMask.nroOfRooms = rooms.length || null;
      ctx.searchMask.category = sm.category ?? null;
      ctx.selectedCampaignCode = this.uniqStrings([sm.bookingCode]);
    }

    const cartItems = this.shopCart.getCart()?.getCartItemsByType('roomrate') ?? [];
    ctx.selectedRoomCode = this.uniqStrings(cartItems.map((i) => i.item?.room?.roomCode));
    ctx.selectedRoomCategory = this.uniqStrings(cartItems.map((i) => i.item?.room?.roomCategory));
    ctx.selectedRateCode = this.uniqStrings(cartItems.map((i) => i.item?.rate?.rateCode));
    ctx.selectedRateCategory = this.uniqStrings(cartItems.map((i) => i.item?.rate?.rateCategory));
    ctx.selectedExtraCode = this.uniqStrings(
      cartItems.flatMap((i) => (i.extras ?? []).map((e: IExtraCartItem) => e.rateId))
    );
    // DEPARTMENTCODE is delivered by the availability search under the name roomCategory,
    // so it shares the same source as selectedRoomCategory above.
    ctx.selectedDepartmentCode = this.uniqStrings(cartItems.map((i) => i.item?.room?.roomCategory));
    ctx.selectedAllotmentCode = this.avService.getSelectedAllotmentCodes();

    return ctx;
  }

  /** Dedup an array of possibly-empty strings, dropping falsy values. */
  private uniqStrings(arr: (string | undefined | null)[]): string[] {
    return Array.from(new Set(arr.filter((v): v is string => !!v)));
  }

  mapNotificationColor(color:string):"error" | "success" | "info" | "warn" | "secondary" | "contrast" | null | undefined {
    let severity:"error" | "success" | "info" | "warn" | "secondary" | "contrast" | null | undefined = 'secondary';
    switch (color) {
      case "red":
        severity = 'error'
        break;
      case "yellow":
        severity = 'warn'
        break;
      case "green":
        severity = 'success'
        break;
      case "blue":
        severity = 'info'
        break;
      case "theme":
        severity = 'secondary'
        break;
      case "theme-contrast":
        severity = 'contrast'
        break;
      default:
        break;
    }
    return severity;
  }
}
